import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';

export type MeioPagamento = 'PIX' | 'CARTAO';

/**
 * Extrai uma MENSAGEM (string) de um erro do Apollo/JS. É obrigatório porque
 * dispatchGlobalError espera string e o ErrorAlert renderiza `{error}` direto —
 * passar o objeto de erro cru fazia o React quebrar com "Objects are not valid as
 * a React child", derrubando a tela (tela branca no pagamento).
 */
function extrairMensagemErro(error: unknown): string {
  const anyErr = error as any;
  return (
    anyErr?.graphQLErrors?.[0]?.message ??
    anyErr?.networkError?.result?.errors?.[0]?.message ??
    anyErr?.message ??
    'Erro ao processar a solicitação. Tente novamente.'
  );
}

export interface EnderecoCobrancaInput {
  cep: string;
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
  pais: string;
  complemento?: string;
}

export interface DadosCartaoInput {
  numero: string;
  nomeImpresso: string;
  validadeMes: number;
  validadeAno: number;
  cvv: string;
}

export interface FinalizarPagamentoAgendamentoInput {
  meioPagamento: MeioPagamento;
  enderecoCobranca: EnderecoCobrancaInput;
  dadosCartao?: DadosCartaoInput;
}

export interface AgendamentoPixInfo {
  qrcodeText: string | null;
  qrcodeImage: string | null;
  copyAndPasteKey: string | null;
  secureUrl: string | null;
}

export interface AgendamentoPagamentoResult {
  agendamento: {
    id: string;
    status: string;
    invoiceId: string | null;
    meioPagamento: string | null;
  };
  confirmado: boolean;
  pixInfo: AgendamentoPixInfo | null;
}

const FINALIZAR_PAGAMENTO_AGENDAMENTO = gql`
  mutation FinalizarPagamentoAgendamento($agendamentoId: ID!, $input: FinalizarPagamentoInput!) {
    finalizarPagamentoAgendamento(agendamentoId: $agendamentoId, input: $input) {
      agendamento {
        id
        status
        invoiceId
        meioPagamento
      }
      confirmado
      pixInfo {
        qrcodeText
        qrcodeImage
        copyAndPasteKey
        secureUrl
      }
    }
  }
`;

const FINALIZAR_PAGAMENTO_PEDIDO = gql`
  mutation FinalizarPagamentoPedido($pedidoId: ID!, $input: FinalizarPagamentoInput!) {
    finalizarPagamentoPedido(pedidoId: $pedidoId, input: $input) {
      agendamento {
        id
        status
        invoiceId
        meioPagamento
      }
      confirmado
      pixInfo {
        qrcodeText
        qrcodeImage
        copyAndPasteKey
        secureUrl
      }
    }
  }
`;

export async function finalizarPagamentoAgendamento(
  agendamentoId: string,
  input: FinalizarPagamentoAgendamentoInput
): Promise<AgendamentoPagamentoResult> {
  try {
    const { data } = await apolloClient.mutate({
      mutation: FINALIZAR_PAGAMENTO_AGENDAMENTO,
      variables: { agendamentoId, input },
    });
    return data.finalizarPagamentoAgendamento;
  } catch (error) {
    dispatchGlobalError(extrairMensagemErro(error));
    throw error;
  }
}

export async function finalizarPagamentoPedido(
  pedidoId: string,
  input: FinalizarPagamentoAgendamentoInput
): Promise<AgendamentoPagamentoResult> {
  try {
    const { data } = await apolloClient.mutate({
      mutation: FINALIZAR_PAGAMENTO_PEDIDO,
      variables: { pedidoId, input },
    });
    return data.finalizarPagamentoPedido;
  } catch (error) {
    dispatchGlobalError(extrairMensagemErro(error));
    throw error;
  }
}
